import { useAuth } from "@clerk/clerk-react";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { Campaign, CampaignBrand, CampaignStats } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

export interface GetCampaignResponse {
  campaign: Campaign;
  campaignBrands: CampaignBrand[];
  brandsCount: number;
}

interface CampaignsContextInterface {
  campaigns: Campaign[];
  setCampaigns: Dispatch<SetStateAction<Campaign[]>>;
  campaignPage: number;
  setCampaignPage: Dispatch<SetStateAction<number>>;
  campaignsCount: number | null;
  setCampaignsCount: Dispatch<SetStateAction<number | null>>;
  getCampaigns: (refreshList?: boolean) => Promise<void>;
  campaignsLoading: boolean;
  setCampaignsLoading: Dispatch<SetStateAction<boolean>>;
  campaignStats: { [key in number]: CampaignStats };
  campaignStatsLoading: { [key in number]: boolean };
  deleteCampaign: (campaignId: number) => Promise<void>;
  addCampaign: (campaign: Campaign) => void;
  getCampaign: (
    campaignId: number,
    page?: number,
    perPage?: number,
  ) => Promise<GetCampaignResponse | null>;
}

const defaultInterface = {
  campaigns: [],
  setCampaigns: defaultContextMissingFunction,
  campaignPage: 1,
  setCampaignPage: defaultContextMissingFunction,
  campaignsCount: null,
  setCampaignsCount: defaultContextMissingFunction,
  getCampaigns: defaultContextMissingFunction,
  campaignsLoading: false,
  setCampaignsLoading: defaultContextMissingFunction,
  campaignStats: {},
  campaignStatsLoading: {},
  deleteCampaign: defaultContextMissingFunction,
  addCampaign: defaultContextMissingFunction,
  getCampaign: defaultContextMissingFunction,
};

const CampaignsContext =
  createContext<CampaignsContextInterface>(defaultInterface);

interface CampaignsProviderProps {
  children: React.ReactNode;
}

const CampaignsProvider = ({ children }: CampaignsProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignPage, setCampaignPage] = useState<number>(1);
  const [campaignStats, setCampaignStats] = useState<{
    [key in number]: CampaignStats;
  }>({});
  const [campaignsCount, setCampaignsCount] = useState<number | null>(null);
  const [campaignsLoading, setCampaignsLoading] = useState<boolean>(false);
  const [campaignStatsLoading, setCampaignStatsLoading] = useState<{
    [key in number]: boolean;
  }>({});

  const getCampaignStats = async (campaignIds: number[]) => {
    if (campaignIds.length === 0 || !currentOrg?.id) {
      return;
    }

    setCampaignStatsLoading((prev) => ({
      ...prev,
      ...campaignIds.reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    }));

    const params = new URLSearchParams();
    campaignIds.forEach((id) => params.append("campaign_ids", id.toString()));
    const url = `/api/organization/${
      currentOrg?.id
    }/campaigns/stats?${params.toString()}`;

    try {
      const { campaignStats } = await fetcherAuth(getToken, url, "GET");
      const statsById = campaignIds.reduce(
        (acc, id, index) => {
          acc[id] = campaignStats[index];
          return acc;
        },
        {} as Record<number, CampaignStats>,
      );
      setCampaignStats((prev) => ({
        ...prev,
        ...statsById,
      }));
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCampaignStatsLoading((prev) => ({
        ...prev,
        ...campaignIds.reduce(
          (acc, id) => {
            acc[id] = false;
            return acc;
          },
          {} as Record<number, boolean>,
        ),
      }));
    }
  };

  const getCampaigns = async (refreshList: boolean = false) => {
    if (!currentOrg?.id) {
      return;
    }

    let page = campaignPage;

    if (refreshList) {
      page = 1;
      setCampaignPage(1);
    }

    setCampaignsLoading(true);
    let url = `/api/organization/${currentOrg?.id}/campaigns?page=${page}`;

    try {
      const { campaigns, count } = await fetcherAuth(getToken, url, "GET");
      if (page === 1) {
        setCampaigns(campaigns);
      } else {
        setCampaigns((prev) => [...prev, ...campaigns]);
      }
      getCampaignStats(campaigns.map((c: Campaign) => c.id));
      setCampaignPage(page + 1);
      setCampaignsCount(count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const addCampaign = (campaign: Campaign) => {
    setCampaigns((prev) => [...prev, campaign]);
    getCampaignStats([campaign.id]);
  };

  const deleteCampaign = async (campaignId: number) => {
    if (!currentOrg?.id) {
      return;
    }

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/delete`,
        "POST",
        {},
        {
          campaignIds: [campaignId],
        },
      );
      setCampaigns((prev) => {
        return prev.filter((c) => c.id !== campaignId);
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getCampaign = async (
    campaignId: number,
    page: number = 1,
    perPage: number = 3,
  ) => {
    if (!currentOrg?.id) {
      return null;
    }

    try {
      const { campaign, campaignBrands, brandsCount } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/${campaignId}?page=${page}&per_page=${perPage}`,
        "GET",
      );
      return { campaign, campaignBrands, brandsCount };
    } catch (error) {
      setErrorAlert(error);
    }
    return null;
  };

  return (
    <CampaignsContext.Provider
      value={{
        campaigns,
        setCampaigns,
        campaignPage,
        setCampaignPage,
        campaignsCount,
        setCampaignsCount,
        getCampaigns,
        campaignsLoading,
        setCampaignsLoading,
        campaignStats,
        campaignStatsLoading,
        deleteCampaign,
        addCampaign,
        getCampaign,
      }}
    >
      {children}
    </CampaignsContext.Provider>
  );
};

export { CampaignsProvider, CampaignsContext };
